// src/components/facilities/venues-api/VenueApiList.tsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { VenueApiResult } from '../../../types/venue';
import { VenueSearchSection } from './VenueSearchSection';
import { useVenueSearch } from './hooks/useVenueSearch';
import { useVenueDistance } from './hooks/useVenueDistance';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { useAddressSearch } from './hooks/useAddressSearch';
import { sortVenues } from '../../../lib/utils/venue-sorting';
import type { VenueSortOption } from '../../../lib/utils/venue-sorting';
import { getUserLocation, DEFAULT_LOCATION } from '../../../lib/utils/location';
import { cn } from '@/lib/utils';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/contexts/AuthContext';
import { avatarCache } from '@/lib/utils/avatarCache';
import { ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import VenueListItem from './components/VenueListItem';
import VenueListControls from './components/VenueListControls';
import SimpleMap from '../maps/SimpleMap';
import VenueGallery from '../maps/VenueGallery';

interface VenueApiListProps {
  city: string;
}

const ITEMS_PER_PAGE = 40;
const DISTANCE_THRESHOLD_KM = 50;

function getDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function VenueApiList({ city }: VenueApiListProps) {
  const [sortBy, setSortBy] = useState<VenueSortOption>('distance');
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isLocationChecked, setIsLocationChecked] = useState(false);
  const [userReferencePoint, setUserReferencePoint] = useState<{ lat: number; lng: number } | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string>();
  const [inCity, setInCity] = useState(false);
  const [availableActivities, setAvailableActivities] = useState<Set<string>>(new Set());
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [hasSeenScrollIndicator, setHasSeenScrollIndicator] = useState(() =>
    localStorage.getItem('hasSeenVenueScrollIndicator') === 'true'
  );
  const scrollTimerRef = useRef<NodeJS.Timeout>();
  const hasShownIndicatorRef = useRef(false);
  const [selectedVenueId, setSelectedVenueId] = useState<string | null>(null);

  const { user } = useAuth();
  const { profile } = useProfile();
  useSwipeGesture(viewMode === 'map');

  useEffect(() => {
    const checkPermissionAndSetLocation = async () => {
      try {
        const loc = await getUserLocation();
        setUserReferencePoint({ lat: loc.latitude, lng: loc.longitude });
      } catch {
        setUserReferencePoint(DEFAULT_LOCATION);
      } finally {
        setIsLocationChecked(true);
      }
    };
    checkPermissionAndSetLocation();
  }, []);

  useEffect(() => {
    if (user?.id) {
      const cached = avatarCache.getAvatar(user.id);
      setAvatarUrl(cached || avatarCache.getDefaultAvatar());
    }
  }, [user?.id]);

  const {
    searchTerm,
    setSearchTerm,
    filteredVenues,
    isLoading: isVenuesLoading
  } = useVenueSearch(city);

  useEffect(() => {
    const acts = new Set<string>();
    filteredVenues.forEach(v => v.activities?.forEach(a => acts.add(a)));
    setAvailableActivities(acts);
  }, [filteredVenues]);

  const {
    address,
    referencePoint,
    isLoading: isAddressLoading,
    error: addressError,
    setAddress,
    setReferencePoint,
    resetLocation
  } = useAddressSearch();

  const memoizedVenues = useMemo(() => filteredVenues, [filteredVenues]);
  const distances = useVenueDistance(
    isLocationChecked ? memoizedVenues : [],
    referencePoint || userReferencePoint
  );
  const sortedVenues = useMemo(() => {
  const sorted = sortVenues(memoizedVenues, sortBy, distances);
  // Pull out any item that is type === 'city' and push it to the front
  const cityMatch = sorted.find(v => v.type === 'city');
  const otherVenues = sorted.filter(v => v.type !== 'city');

  return cityMatch ? [cityMatch, ...otherVenues] : sorted;
}, [memoizedVenues, sortBy, distances]);

  const {
    page,
    loading: isLoadingMore,
    hasMore,
    lastElementRef,
    reset: resetScroll
  } = useInfiniteScroll({ totalItems: sortedVenues.length, itemsPerPage: ITEMS_PER_PAGE });
  const displayedVenues = useMemo(
    () => sortedVenues.slice(0, page * ITEMS_PER_PAGE),
    [sortedVenues, page]
  );

  useEffect(() => {
    if (userReferencePoint && sortedVenues.length > 0) {
      const center = sortedVenues[0].coordinates;
      const dist = getDistanceKm(
        userReferencePoint.lat,
        userReferencePoint.lng,
        center.lat,
        center.lng
      );
      setInCity(dist <= DISTANCE_THRESHOLD_KM);
    }
  }, [userReferencePoint, sortedVenues]);

  const handleRefresh = useCallback(async () => {
    resetScroll();
    setSearchTerm('');
    setSelectedTags([]);
    setSortBy('distance');
    await resetLocation();
  }, [resetScroll, setSearchTerm, setSelectedTags, setSortBy, resetLocation]);

  const handleTagSelect = useCallback((tag: string) => {
    setSelectedTags(prev => (prev.includes(tag) ? [] : [tag]));
    setSearchTerm(tag);
  }, [setSearchTerm]);

  useEffect(() => {
    if (
      filteredVenues.length > 0 &&
      !hasSeenScrollIndicator &&
      !hasShownIndicatorRef.current
    ) {
      hasShownIndicatorRef.current = true;
      clearTimeout(scrollTimerRef.current!);
      scrollTimerRef.current = setTimeout(() => {
        setShowScrollIndicator(true);
        scrollTimerRef.current = setTimeout(() => {
          setShowScrollIndicator(false);
          localStorage.setItem('hasSeenVenueScrollIndicator', 'true');
          setHasSeenScrollIndicator(true);
        }, 5000);
      }, 1000);
    }
    return () => clearTimeout(scrollTimerRef.current!);
  }, [filteredVenues.length, hasSeenScrollIndicator]);

  if (isVenuesLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 text-gold-400 animate-spin rounded-full border-2 border-current border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <VenueSearchSection
        searchTerm={searchTerm}
        totalVenues={filteredVenues.length}
        availableActivities={Array.from(availableActivities)}
        onSearchChange={setSearchTerm}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onRefresh={handleRefresh}
        isLoading={isAddressLoading}
        address={address}
        onAddressChange={setAddress}
        onLocationSelect={setReferencePoint}
        addressError={addressError}
        selectedTags={selectedTags}
        onTagSelect={handleTagSelect}
      />

      {showScrollIndicator && !hasSeenScrollIndicator && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className={cn(
            "fixed left-1/2 bottom-24 z-50 -translate-x-1/2",
            "flex flex-col items-center gap-2",
            "pointer-events-none md:hidden"
          )}
        >
          <div
            className={cn(
              "text-sm px-3 py-1 rounded-full",
              "dark:text-dark-800 dark:bg-dark-200/80",
              "light:text-light-900 light:bg-light-100/80",
              "backdrop-blur-sm"
            )}
          >
            Scroll to see results
          </div>
          <ChevronDown
            className={cn(
              "h-6 w-6",
              "dark:text-gold-400 light:text-gold-600",
              "animate-bounce"
            )}
          />
        </motion.div>
      )}

      {viewMode === 'map' ? (
        <div className="w-full flex flex-col" data-no-swipe>
          {/* cancel the parent padding so map exactly fills the grey box */}
          <div className="-mx-4 lg:-mx-8">
            <div className="relative w-full h-[calc(100vh-20rem)]">
              <SimpleMap
                venues={sortedVenues}
                className="w-full h-full"
                onMarkerClick={setSelectedVenueId}
                selectedVenueId={selectedVenueId}
                userLocation={userReferencePoint}
                userAvatarUrl={inCity ? avatarUrl ?? null : null}
              />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full border-t border-dark-300"
          >
            <VenueGallery
              venues={sortedVenues}
              selectedVenueId={
                selectedVenueId || sortedVenues[0]?.id || null
              }
              onSelectVenue={setSelectedVenueId}
              onClose={() => {}}
            />
          </motion.div>
        </div>
      ) : (
        <div ref={searchResultsRef}>
          {displayedVenues.map((venue, index) => (
            <VenueListItem
              key={venue.id}
              venue={venue}
              distance={distances[venue.id]}
              isLast={index === displayedVenues.length - 1}
              lastElementRef={lastElementRef}
            />
          ))}

          {isLoadingMore && hasMore && (
            <div className="flex justify-center py-6 mt-4">
              <div className="h-6 w-6 text-gold-400 animate-spin rounded-full border-2 border-current border-t-transparent" />
            </div>
          )}

          {!isLoadingMore && displayedVenues.length === 0 && (
            <div className="text-center py-12 bg-dark-300 rounded-xl">
              <h3 className="text-lg font-medium text-white mb-2">
                No Venues Found
              </h3>
              <p className="text-dark-800">
                Try adjusting your search filters
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
